import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Archive, Loader2, RotateCcw, Trash2, Calendar, Database } from "lucide-react";
import { toast } from "sonner";
import { confirmDialog } from "../../utils/confirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
};

const AutoBackupsTab = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);

  const fetchBackups = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/auto-backups`);
      setBackups(data || []);
    } catch {
      toast.error("فشل تحميل النسخ");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBackups(); }, []);

  const handleRestore = async (bk) => {
    const first = await confirmDialog(
      `⚠️ استعادة النسخة المؤرخة ${fmtDate(bk.created_at)}؟\n\nسيتم استبدال البيانات الحالية (طلبات، زبائن، سائقين، مشرفين، مجاهزين) بمحتوى النسخة.\nالمنتجات والأقسام والشركات تبقى كما هي.`
    );
    if (!first) return;
    const confirmText = window.prompt('للتأكيد، اكتب: "استعادة"');
    if (confirmText !== "استعادة") {
      toast.error("تم إلغاء العملية");
      return;
    }
    setRestoring(bk.id);
    try {
      const { data } = await axios.post(`${API}/admin/auto-backups/${bk.id}/restore`);
      const total = Object.values(data.restored || {}).reduce((a, b) => a + b, 0);
      toast.success(`✅ تم استعادة ${total} سجل من النسخة`, { duration: 7000 });
      fetchBackups();
    } catch (err) {
      toast.error(err.response?.data?.detail || "فشل الاستعادة");
    } finally {
      setRestoring(null);
    }
  };

  const handleDelete = async (bk) => {
    if (!(await confirmDialog(`حذف هذه النسخة الآمنة المؤرخة ${fmtDate(bk.created_at)}؟`))) return;
    try {
      await axios.delete(`${API}/admin/auto-backups/${bk.id}`);
      toast.success("تم الحذف");
      fetchBackups();
    } catch {
      toast.error("فشل الحذف");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="auto-backups-tab">
      <div className="bg-gradient-to-l from-[#1E3A5F] to-[#3B82F6] text-white rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-1">
          <Archive className="w-5 h-5" />
          <h3 className="font-bold text-lg">النسخ الآمنة قبل التصفير</h3>
        </div>
        <p className="text-xs opacity-90">
          كل مرة تضغط زر "تصفير الطلبات" يتم حفظ نسخة احتياطية كاملة من البيانات قبل الحذف.
          يمكنك استعادتها متى ما تراجعت. النظام يحتفظ بآخر <span dir="ltr" className="font-bold">10</span> نسخ.
        </p>
      </div>

      {backups.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-[#BAE6FD] rounded-2xl p-10 text-center">
          <Archive className="w-12 h-12 mx-auto text-[#BAE6FD] mb-2" />
          <p className="text-[#5C6E64]">لا توجد نسخ آمنة بعد</p>
          <p className="text-xs text-[#5C6E64] mt-1">سيتم إنشاء نسخة تلقائياً عند أول تصفير</p>
        </div>
      ) : (
        <div className="space-y-2">
          {backups.map((bk, i) => (
            <motion.div
              key={bk.id}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              data-testid={`auto-backup-${bk.id}`}
              className="bg-white border border-[#E4EBE6] rounded-2xl p-4 shadow-sm"
            >
              <div className="flex items-start gap-3">
                <div className="bg-[#F0F9FF] border-2 border-[#3B82F6] rounded-full w-12 h-12 flex items-center justify-center shrink-0">
                  <Database className="w-5 h-5 text-[#3B82F6]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="font-bold text-[#1E3A5F]">{bk.label || "نسخة آمنة"}</p>
                    {i === 0 && (
                      <span className="bg-[#7ED321] text-white text-[9px] font-black px-2 py-0.5 rounded-full">
                        الأحدث
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-[#5C6E64] flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span dir="ltr">{fmtDate(bk.created_at)}</span>
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {Object.entries(bk.counts || {}).filter(([, n]) => n > 0).map(([k, n]) => (
                      <span key={k} className="bg-[#F3F6F4] text-[#1E3A5F] text-[10px] font-bold px-2 py-0.5 rounded-full">
                        {k}: <span dir="ltr">{n}</span>
                      </span>
                    ))}
                  </div>
                  <p className="text-[10px] text-[#5C6E64] mt-1">
                    إجمالي السجلات: <span dir="ltr" className="font-bold">{bk.total_documents || 0}</span>
                  </p>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={() => handleRestore(bk)}
                  disabled={restoring === bk.id}
                  data-testid={`auto-backup-restore-${bk.id}`}
                  className="flex-1 bg-[#7ED321] hover:bg-[#6cb91d] disabled:opacity-50 text-white font-bold text-xs py-2 rounded-full flex items-center justify-center gap-1"
                >
                  {restoring === bk.id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <RotateCcw className="w-3 h-3" />
                  )}
                  استعادة هذه النسخة
                </button>
                <button
                  onClick={() => handleDelete(bk)}
                  data-testid={`auto-backup-delete-${bk.id}`}
                  className="bg-[#E85D45] hover:bg-[#c14a36] text-white px-3 py-2 rounded-full text-xs flex items-center gap-1"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AutoBackupsTab;
